import React, { useState } from 'react';
import { Container, Typography, Modal, Button } from '@sm/wds-react';
import { InformationModalSkeletonLoader } from './InformationModalSkeleton';
import './information-modal.scss';

export type InformationModalProps = {
  headline: string;
  subHeadline: string;
  message: string | React.ReactElement;
  primaryCtaText?: string;
  primaryCtaLink?: string;
  primaryCtaNewTab?: boolean;
  primaryCtaColor?: string;
  primaryCtaVariant?: string;
  dismissCtaText: string;
  dismissCtaColor?: string;
  dismissCtaVariant?: string;
  image: () => React.ReactElement | null;
  loading?: boolean;
};

export const InformationModal: React.FC<InformationModalProps> = ({
  headline,
  subHeadline,
  message,
  primaryCtaText,
  primaryCtaLink,
  primaryCtaNewTab,
  primaryCtaColor,
  primaryCtaVariant,
  dismissCtaText,
  dismissCtaColor,
  dismissCtaVariant,
  image,
  loading,
}) => {
  const ImageComponent = image;
  const [show, changeVisibility] = useState(true);
  const templateClass = 'sm-information-modal';
  const handleOnClick = () => {
    changeVisibility(!show);
  };

  return (
    <Modal show={show} size="lg" onClose={handleOnClick} dismissible={!loading}>
      {loading ? (
        <InformationModalSkeletonLoader />
      ) : (
        <Container className={templateClass} flex>
          <ImageComponent />
          <Container flex flexDirection="column" p="6">
            <Typography
              className={`${templateClass}__headline wds-m-y-2`}
              variant="pageTitle"
            >
              <strong data-testid="headline">{headline}</strong>
            </Typography>
            <Typography
              data-testid="sub-headline"
              className={`${templateClass}__sub-headline wds-m-y-2`}
              component="section"
              variant="sectionTitle"
            >
              {subHeadline}
            </Typography>
            <Typography
              data-testid="message"
              className="wds-m-y-3 wds-flex__item--grow"
            >
              {message}
            </Typography>
            <Container flex flexJustify="end" flexAlign="center">
              <Button
                data-testid="dismiss-cta"
                color={dismissCtaColor}
                variant={dismissCtaVariant}
                onClick={handleOnClick}
              >
                {dismissCtaText}
              </Button>
              {primaryCtaText && (
                <Button
                  className="wds-m-l-4"
                  color={primaryCtaColor}
                  variant={primaryCtaVariant}
                  data-testid="primary-cta"
                  href={primaryCtaLink}
                  target={primaryCtaNewTab ? '_blank' : undefined}
                  rel={primaryCtaNewTab ? 'noreferrer noopener' : undefined}
                >
                  {primaryCtaText}
                </Button>
              )}
            </Container>
          </Container>
        </Container>
      )}
    </Modal>
  );
};

InformationModal.defaultProps = {
  loading: false,
  primaryCtaNewTab: false,
  primaryCtaColor: 'primary',
  dismissCtaColor: 'alt',
};

export default InformationModal;
